import { useState, useEffect } from 'react';
import { Code2, Copy, Check } from 'lucide-react';

import { Button } from '../../ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../ui/popover';
import { getAuthHeaders, adminFetch } from '../../../services/adminApi';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function MergeTagPicker({ onInsert }) {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedTag, setCopiedTag] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    try {
      const response = await adminFetch(`${API_BASE}/api/admin/newsletter/merge-tags`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setTags(data);
      }
    } catch (error) {
      console.error('Error fetching merge tags:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInsert = (tag) => {
    if (onInsert) {
      onInsert(tag.tag);
    }
    setCopiedTag(tag.name);
    setTimeout(() => setCopiedTag(null), 1500);
  };

  const handleCopy = (tag, e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(tag.tag);
    setCopiedTag(tag.name);
    setTimeout(() => setCopiedTag(null), 1500);
  };

  // Group tags by category
  const recipientTags = tags.filter(t => ['first_name', 'full_name', 'email', 'member_since'].includes(t.name));
  const studentTags = tags.filter(t => ['student_name', 'belt_rank', 'program', 'next_class'].includes(t.name));
  const studioTags = tags.filter(t => ['studio_name', 'studio_phone', 'studio_address'].includes(t.name));

  const TagGroup = ({ title, groupTags }) => (
    groupTags.length > 0 && (
      <div className="mb-3">
        <p className="text-xs font-medium text-gray-500 mb-1.5">{title}</p>
        <div className="space-y-1">
          {groupTags.map(tag => (
            <div
              key={tag.name}
              className="flex items-center justify-between p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer group"
              onClick={() => handleInsert(tag)}
            >
              <div className="flex-1 min-w-0">
                <code className="text-xs font-mono text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded">
                  {tag.tag}
                </code>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">{tag.description}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100"
                onClick={(e) => handleCopy(tag, e)}
              >
                {copiedTag === tag.name ? (
                  <Check className="h-3 w-3 text-green-600" />
                ) : (
                  <Copy className="h-3 w-3" />
                )}
              </Button>
            </div>
          ))}
        </div>
      </div>
    )
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5">
          <Code2 className="h-3.5 w-3.5" />
          Merge Tags
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3" align="start">
        <div className="mb-3">
          <h4 className="font-medium text-sm">Insert Merge Tag</h4>
          <p className="text-xs text-gray-500 mt-0.5">
            Click a tag to insert it at the cursor position
          </p>
        </div>

        {loading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading tags...</p>
        ) : tags.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No merge tags available</p>
        ) : (
          <div className="max-h-[300px] overflow-y-auto">
            <TagGroup title="Recipient Info" groupTags={recipientTags} />
            <TagGroup title="Student Info" groupTags={studentTags} />
            <TagGroup title="Studio Info" groupTags={studioTags} />
          </div>
        )}

        <div className="mt-3 pt-3 border-t">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Tags are replaced with actual values when the email is sent.
            If data is missing, a fallback value is used.
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
